import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import { FiUserCheck, FiUser, FiMapPin, FiFileText } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { validarCPFouCNPJ, formatarDocumento, ehCNPJ } from '@ponti/shared/utils';
import { useCamposCadastro, inputTypeDoCampo } from '../../hooks/useCamposCadastro';

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

export function CompletarCadastroPage() {
  const { cidadao, session, recarregarCidadao } = useAuth();
  const navigate = useNavigate();

  // Documento: CPF (pessoa) OU CNPJ (empresa). Pré-preenche com o que já existir no cadastro.
  const [documento, setDocumento] = useState(
    cidadao?.cnpj ? formatarDocumento(cidadao.cnpj) : (cidadao?.cpf ? formatarDocumento(cidadao.cpf) : ''),
  );
  const [docErro, setDocErro] = useState<string | null>(null);
  const [nomeSocial, setNomeSocial] = useState(cidadao?.nome_social ?? '');
  const [telefone, setTelefone] = useState(cidadao?.telefone ?? '');
  const [cep, setCep] = useState(cidadao?.cep ?? '');
  const [logradouro, setLogradouro] = useState(cidadao?.logradouro ?? '');
  const [numero, setNumero] = useState(cidadao?.numero ?? '');
  const [bairro, setBairro] = useState(cidadao?.bairro ?? '');
  const [cidade, setCidade] = useState(cidadao?.cidade ?? '');
  const [uf, setUf] = useState(cidadao?.uf ?? '');
  const [complemento, setComplemento] = useState(cidadao?.complemento ?? '');

  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  // Config de campos do manifestante definida pela prefeitura (item 2.27).
  const [config, setConfig] = useState<Record<string, string>>({ telefone: 'obrigatorio', endereco: 'opcional' });
  const camposCustom = useCamposCadastro();
  const [valoresCustom, setValoresCustom] = useState<Record<string, string>>(
    (cidadao?.campos_customizados as Record<string, string> | undefined) ?? {},
  );

  useEffect(() => {
    supabase.from('estabelecimentos').select('configuracoes').eq('id', getEstabelecimentoAtualId()).maybeSingle()
      .then(({ data }) => {
        const cfg = data?.configuracoes as { campos_manifestante?: Record<string, string> } | undefined;
        if (cfg?.campos_manifestante) setConfig((p) => ({ ...p, ...cfg.campos_manifestante }));
      });
  }, []);

  function validarCampoDoc(valor: string): boolean {
    const digitos = valor.replace(/\D/g, '');
    if (digitos.length === 0) { setDocErro(null); return false; }
    if (!validarCPFouCNPJ(valor)) {
      setDocErro(digitos.length > 11 ? 'CNPJ inválido — confira os dígitos.' : 'CPF inválido — confira os dígitos.');
      return false;
    }
    setDocErro(null);
    return true;
  }

  function onChangeDoc(valor: string) {
    const formatado = formatarDocumento(valor);
    setDocumento(formatado);
    const n = formatado.replace(/\D/g, '').length;
    if (n === 11 || n === 14) validarCampoDoc(formatado);
    else setDocErro(null);
  }

  async function buscarCep() {
    const limpo = cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
      const d = await r.json();
      if (d.erro) {
        setErro('CEP não encontrado.');
        return;
      }
      setLogradouro(d.logradouro ?? '');
      setBairro(d.bairro ?? '');
      setCidade(d.localidade ?? '');
      setUf(d.uf ?? '');
    } catch {
      setErro('Falha ao consultar o CEP.');
    } finally {
      setBuscandoCep(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const userId = cidadao?.id ?? session?.user?.id;
    if (!userId) {
      setErro('Sessão expirada. Faça login novamente.');
      return;
    }

    if (!documento.trim()) {
      setErro('Informe seu CPF ou CNPJ.');
      return;
    }
    if (!validarCPFouCNPJ(documento)) {
      setErro('CPF/CNPJ inválido — confira os dígitos.');
      return;
    }
    if (config.telefone === 'obrigatorio' && telefone.replace(/\D/g, '').length < 10) {
      setErro('Informe um telefone válido.');
      return;
    }
    if (config.endereco === 'obrigatorio' && !cidade) {
      setErro('Informe sua cidade.');
      return;
    }
    const campoFaltando = camposCustom.find((c) => c.obrigatorio && !(valoresCustom[c.id]?.trim()));
    if (campoFaltando) {
      setErro(`Preencha o campo "${campoFaltando.nome}".`);
      return;
    }

    // Nome/e-mail: do perfil existente ou dos metadados do login (ex.: Google).
    const meta = (session?.user?.user_metadata ?? {}) as Record<string, unknown>;
    const nome = cidadao?.nome
      ?? (meta.full_name as string | undefined)
      ?? (meta.name as string | undefined)
      ?? session?.user?.email?.split('@')[0]
      ?? 'Cidadão';
    const email = cidadao?.email ?? session?.user?.email ?? '';

    const digitos = documento.replace(/\D/g, '');
    const empresa = ehCNPJ(documento);

    setErro(null);
    setCarregando(true);
    // upsert: cria a linha (1º acesso via Google) ou atualiza a existente.
    const { error } = await supabase
      .from('cidadaos')
      .upsert({
        id: userId,
        nome,
        nome_social: nomeSocial.trim() || null,
        email,
        cpf: empresa ? null : digitos,
        cnpj: empresa ? digitos : null,
        telefone: telefone.replace(/\D/g, ''),
        cep: cep.replace(/\D/g, '') || null,
        logradouro: logradouro || null,
        numero: numero || null,
        complemento: complemento || null,
        bairro: bairro || null,
        cidade,
        uf: uf || null,
        campos_customizados: valoresCustom,
      }, { onConflict: 'id' });
    setCarregando(false);

    if (error) {
      setErro(error.message);
      return;
    }
    await recarregarCidadao();
    navigate('/');
  }

  return (
    <PortalPage
      titulo="Complete seu cadastro"
      subtitulo="Estes dados serão usados para identificar suas solicitações junto à prefeitura."
      icone={<FiUserCheck size={26} />}
      voltarHref="/"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {erro && <Alert variante="erro">{erro}</Alert>}

        <SectionCard titulo="Identificação" icone={<FiUser size={18} />}>
          <div className="space-y-5">
            <div>
              <Label htmlFor="nome_social">Nome social</Label>
              <Input id="nome_social" value={nomeSocial} onChange={(e) => setNomeSocial(e.target.value)} placeholder="Como você prefere ser chamado(a)" />
              <p className="text-xs text-slate-500 mt-1">Opcional — Decreto 8.727/2016.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="documento" obrigatorio>CPF / CNPJ</Label>
                <Input
                  id="documento"
                  inputMode="numeric"
                  invalido={!!docErro}
                  value={documento}
                  onChange={(e) => onChangeDoc(e.target.value)}
                  onBlur={() => validarCampoDoc(documento)}
                  placeholder="CPF (pessoa) ou CNPJ (empresa)"
                  maxLength={18}
                  required
                />
                {docErro
                  ? <p className="text-xs text-red-600 mt-1">{docErro}</p>
                  : <p className="text-xs text-slate-500 mt-1">Pessoa física informe o CPF; empresa/fornecedor informe o CNPJ.</p>}
              </div>

              {config.telefone !== 'oculto' && (
                <div>
                  <Label htmlFor="telefone" obrigatorio={config.telefone === 'obrigatorio'}>Telefone</Label>
                  <Input
                    id="telefone"
                    inputMode="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="(00) 00000-0000"
                    required={config.telefone === 'obrigatorio'}
                  />
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        {config.endereco !== 'oculto' && (
          <SectionCard titulo="Endereço" icone={<FiMapPin size={18} />}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cep">CEP</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cep"
                        inputMode="numeric"
                        value={cep}
                        onChange={(e) => setCep(e.target.value)}
                        onBlur={buscarCep}
                        placeholder="00000-000"
                        maxLength={9}
                      />
                      <Button type="button" variant="outline" loading={buscandoCep} onClick={buscarCep}>
                        Buscar
                      </Button>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="logradouro">Logradouro</Label>
                    <Input
                      id="logradouro"
                      value={logradouro}
                      onChange={(e) => setLogradouro(e.target.value)}
                      placeholder="Rua, Avenida..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="numero">Número</Label>
                    <Input
                      id="numero"
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      placeholder="123"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="complemento">Complemento</Label>
                    <Input
                      id="complemento"
                      value={complemento}
                      onChange={(e) => setComplemento(e.target.value)}
                      placeholder="Apto, bloco, fundos..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="bairro">Bairro</Label>
                    <Input
                      id="bairro"
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      placeholder="Centro"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cidade" obrigatorio={config.endereco === 'obrigatorio'}>Cidade</Label>
                    <Input
                      id="cidade"
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      placeholder="Sua cidade"
                      required={config.endereco === 'obrigatorio'}
                    />
                  </div>
                  <div>
                    <Label htmlFor="uf">UF</Label>
                    <select
                      id="uf"
                      value={uf}
                      onChange={(e) => setUf(e.target.value)}
                      className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional"
                    >
                      <option value="">--</option>
                      {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
            </div>
          </SectionCard>
        )}

        {camposCustom.length > 0 && (
          <SectionCard titulo="Informações adicionais" icone={<FiFileText size={18} />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {camposCustom.map((campo) => (
                <div key={campo.id}>
                  <Label htmlFor={`custom-${campo.id}`} obrigatorio={campo.obrigatorio}>{campo.nome}</Label>
                  <Input
                    id={`custom-${campo.id}`}
                    type={inputTypeDoCampo(campo.tipo)}
                    inputMode={campo.tipo === 'numero' ? 'numeric' : undefined}
                    value={valoresCustom[campo.id] ?? ''}
                    onChange={(e) => setValoresCustom((p) => ({ ...p, [campo.id]: e.target.value }))}
                    required={campo.obrigatorio}
                  />
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => navigate('/')}>
            Pular por enquanto
          </Button>
          <Button type="submit" loading={carregando} className="flex-1">
            Salvar e continuar
          </Button>
        </div>
      </form>
    </PortalPage>
  );
}
